import * as React from 'react';
import moment from 'moment';
import propz from 'propz';
import { EventInvitationMessage } from 'Src/models/message';
import { CLUB_STAFF } from 'Src/consts/club';
import { getFullName } from 'Src/helpers/autocomplete/autocomplete';
import { WEEK_DAYS_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/date';
import { SECONDS_IN_MINUTE, MILLISECONDS_IN_SECOND } from 'Src/consts/common';
import {
  getLastResponseUpdatedAtText,
  getRespondedAtText,
  getResponseUser,
  statusChangedAfterFirstResponse,
  wasEditedAfterResponse
} from 'Src/helpers/message/message';
import { QUESTIONS_TEMPLATE_TYPE } from 'Src/consts/message';
import { FormAnswersSummary } from 'Src/components/FormAnswersSummary/FormAnswersSummary';
import { answersToMap } from 'Src/helpers/form/formRuntime';
import './ClubBookingDetails.scss';

interface Props {
  message: EventInvitationMessage;
}

export const ClubBookingDeatils: React.FC<Props> = ({ message }) => {
  if (!message) return null;

  const buildClubSummary = (msg: EventInvitationMessage) => {
    const clubName = propz.get(msg, ['clubData', 'name'], '');

    const placeName = propz.get(msg, ['clubData', 'venue', 'placeData', 'name'], '');
    const postcode = propz.get(msg, ['clubData', 'venue', 'placeData', 'postcode'], '');
    const venue = placeName || postcode || '';

    const startDate = propz.get(msg, ['clubData', 'startDate']);
    const endDate = propz.get(msg, ['clubData', 'finishDate']);
    const start = startDate ? moment(startDate) : null;
    const end = endDate ? moment(endDate) : null;
    const specificDates = (propz.get(msg, ['clubData', 'dates'], []) || []) as string[];
    const intervals = (propz.get(msg, ['clubData', 'intervals'], []) || []) as Array<{ start: string; end: string }>;

    const runRange = specificDates.length
      ? specificDates.filter(date => moment(date).isValid()).sort((a, b) => moment(a).valueOf() - moment(b).valueOf())
          .map(date => moment(date).format('ddd D MMM YYYY')).join(', ')
      : intervals.length
      ? intervals.map(interval => `${moment(interval.start).format('D MMM YYYY')} – ${moment(interval.end).format('D MMM YYYY')}`).join('; ')
      :
      start && end && start.isValid() && end.isValid()
        ? `${start.format('DD MMM YYYY')} – ${end.format('DD MMM YYYY')}`
        : start?.isValid()
        ? start.format('DD MMM YYYY')
        : '';

    const startTimeISO = propz.get(msg, ['clubData', 'time']);
    const durationMin = propz.get(msg, ['clubData', 'duration'], 0);
    const startTs = startTimeISO ? new Date(startTimeISO).getTime() : undefined;
    const endTs = startTs ? startTs + durationMin * SECONDS_IN_MINUTE * MILLISECONDS_IN_SECOND : undefined;

    const timeRange = startTs
      ? `${moment(new Date(startTs).toISOString()).format('HH:mm')}–${moment(new Date(endTs!).toISOString()).format(
          'HH:mm'
        )}`
      : '';

    const weekDays = (propz.get(msg, ['clubData', 'days'], []) as string[])
      .map(d => WEEK_DAYS_SERVER_TO_CLIENT_MAPPING[d] || d)
      .join(', ');

    const when = specificDates.length ? `Specific days${timeRange ? ` · ${timeRange}` : ''}` : weekDays ? `${weekDays} · ${timeRange}` : timeRange;

    return { clubName, venue, runRange, when };
  };

  const { clubName, venue, runRange, when } = buildClubSummary(message);

  const clubDescription = (propz.get(message, ['clubData', 'description'], '') || '').trim();

  const staff = propz.get(message, ['clubData', 'staff'], []) as Array<any>;
  const coaches = (Array.isArray(staff) ? staff : [])
    .filter(s => s?.staffRole === CLUB_STAFF.COACH)
    .map(s => getFullName(s))
    .filter(Boolean)
    .join(', ');

  const members = (Array.isArray(staff) ? staff : [])
    .filter(s => s?.staffRole === CLUB_STAFF.MEMBER_OF_STAFF)
    .map(s => getFullName(s))
    .filter(Boolean)
    .join(', ');

  const respondedAt = getRespondedAtText(message);
  const lastChangedAt = getLastResponseUpdatedAtText(message);
  const responseUser = getResponseUser(message);
  const wasUpdated = wasEditedAfterResponse(message) || statusChangedAfterFirstResponse(message);
  const fields = Array.isArray(message.fields) ? message.fields : [];
  const hasLegacyAnswers = fields.some((field: any) => typeof field?.value !== 'undefined');
  const hasFormAnswers = Array.isArray(message.formAnswers) && message.formAnswers.length > 0;
  const hasResponse = !!(
    message.isActionPerformed ||
    respondedAt ||
    responseUser.fullName ||
    responseUser.email ||
    hasLegacyAnswers ||
    hasFormAnswers
  );
  const initials = (responseUser.fullName || responseUser.email || '?')
    .split(/\s+|@/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('');

  const renderLegacyAnswer = (field: any): React.ReactNode => {
    const { value, type } = field;
    if (typeof value === 'undefined' || value === null || value === '') return '—';
    if (type === QUESTIONS_TEMPLATE_TYPE.BOOLEAN) {
      return value === true || value === 'true' ? 'Yes' : 'No';
    }
    return Array.isArray(value) ? value.join(', ') : String(value);
  };

  return (
    <div className="clubDetails">
      <section className="clubDetails__hero">
        <div className="clubDetails__icon" aria-hidden>
          <span />
        </div>
        <div className="clubDetails__heroText">
          <span className="clubDetails__eyebrow">Club</span>
          <h3>{clubName || 'Untitled club'}</h3>
        </div>
      </section>

      <dl className="clubDetails__facts">
        {runRange ? <div><dt>Run</dt><dd>{runRange}</dd></div> : null}
        {when ? <div><dt>When</dt><dd>{when}</dd></div> : null}
        {venue ? <div><dt>Where</dt><dd>{venue}</dd></div> : null}
        {coaches ? <div><dt>Leader / Coach</dt><dd>{coaches}</dd></div> : null}
        {members ? <div><dt>Member(s) of staff</dt><dd>{members}</dd></div> : null}
      </dl>

      {clubDescription ? (
        <section className="clubDetails__section">
          <h4>About this club</h4>
          <p className="clubDetails__description">{clubDescription}</p>
        </section>
      ) : null}

      <section className="clubDetails__section clubDetails__response">
        <div className="clubDetails__sectionHead">
          <h4>Booking response</h4>
          {wasUpdated ? <span className="clubDetails__status">Edited response</span> : null}
        </div>

        {hasResponse ? (
          <>
            {(responseUser.fullName || responseUser.email) ? (
              <div className="clubDetails__responder">
                <span className="clubDetails__avatar" aria-hidden>{initials}</span>
                <div>
                  <span>Responded by</span>
                  {responseUser.fullName ? <strong>{responseUser.fullName}</strong> : null}
                  {responseUser.email ? <small>{responseUser.email}</small> : null}
                </div>
              </div>
            ) : null}

            {(respondedAt || (wasUpdated && lastChangedAt)) ? (
              <dl className="clubDetails__responseDates">
                {respondedAt ? <div><dt>Responded at</dt><dd>{respondedAt}</dd></div> : null}
                {wasUpdated && lastChangedAt ? <div><dt>Last changed</dt><dd>{lastChangedAt}</dd></div> : null}
              </dl>
            ) : null}

            <div className="clubDetails__answers">
              <h5>Questions and answers</h5>
              {message.formDefinition ? (
                <FormAnswersSummary
                  definition={message.formDefinition}
                  answers={answersToMap(message.formAnswers || [])}
                  emptyText="No questions were included with this invitation."
                />
              ) : fields.length ? (
                <dl className="clubDetails__answerList">
                  {fields.map((field: any, index: number) => (
                    <div key={`${field.heading || 'question'}_${index}`}>
                      <dt>{field.heading || `Question ${index + 1}`}</dt>
                      <dd>{renderLegacyAnswer(field)}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <div className="clubDetails__empty">No questions were included with this invitation.</div>
              )}
            </div>
          </>
        ) : (
          <div className="clubDetails__empty">No response has been submitted yet.</div>
        )}
      </section>

    </div>
  );
};

ClubBookingDeatils.displayName = 'ClubBookingDeatils';



